import * as R from 'ramda';

import {
  CreateJobInput,
  CreateTaskInput,
  DeleteTaskInput,
  DeleteTaskMutation,
  UpdateJobInput,
  _Boolean,
} from '@/API';
import { JobItem } from '@/appsync/graphql/job';
import { TaskItem } from '@/appsync/graphql/task';
import { createJob, updateJob } from '@/appsync/service/JobMutationService';
import { createTask, deleteTask } from '@/appsync/service/TaskMutationService';
import {
  LoginInfo,
  TaskItemForDB,
  TaskMetadataItemForFront,
} from '@/types/type';
import {
  boolean2_BooleanEnum,
  isNotEmptyArray,
  joinWithHash,
  getSHA3OfTenantLandscape,
  isNotEmptyString,
  filterByNotEmptyString,
} from '@/utils/CommonUtils';

export const swapOrder = (tasks: TaskItemForDB[], i: number, j: number) => {
  const jobId = tasks[0].jobId;
  tasks[i].order = i;
  tasks[i].jobIdOrder = joinWithHash(jobId, i.toString());
  tasks[j].order = j;
  tasks[j].jobIdOrder = joinWithHash(jobId, j.toString());
  return tasks;
};

export const updateOrderForAll = (tasks: TaskItemForDB[]) => {
  if (isNotEmptyArray(tasks)) {
    const jobId = tasks[0].jobId;
    tasks.forEach((task, index) => {
      task.order = index;
      task.jobIdOrder = joinWithHash(jobId, index.toString());
    });
  }
  return tasks;
};

const sortByOrder = R.sortBy(R.prop('order'));
const sortedTaskArgs: (task: TaskItemForDB) => any = R.pipe(
  R.prop('args'),
  sortByOrder
);
const sortedTaskMetadataArgs: (task: TaskItemForDB) => any = R.pipe(
  R.pathOr([], ['taskMetadata', 'args']),
  sortByOrder
);

const mergeArray = R.pipe(R.mergeDeepLeft, R.values);

export const getMergeTaskAndMetadata = R.converge(mergeArray, [
  sortedTaskArgs,
  sortedTaskMetadataArgs,
]);

const filterByIsOutputFile = R.filter(R.propEq('isOutputFile', _Boolean._true));

// @ts-ignore FIXME: fix type error
export const getOutputFilePath: (x: TaskItemForDB) => string[] = R.pipe<
  string[]
>(
  getMergeTaskAndMetadata(),
  // @ts-ignore FIXME: fix type error
  filterByIsOutputFile,
  R.lift(R.prop<string>('value')),
  filterByNotEmptyString
);

export const getArrayOfOutputFilePath: (x: TaskItemForDB[]) => any = R.pipe(
  R.map(getOutputFilePath),
  R.flatten
);

export const hasOutputFile: (x: TaskItemForDB[]) => boolean = R.pipe(
  R.map(getOutputFilePath),
  R.flatten,
  R.any(isNotEmptyString)
);

export const callCreateJob = async (data: {
  loginInfo: LoginInfo;
  jobId: string;
  jobTitle: string;
  jobCode: string;
  jobDescription: string;
  jobTriggerFilePath: string;
  addedTasks: TaskItemForDB[];
}) => {
  const createJobInput: CreateJobInput = {
    id: data.jobId,
    jobCode: data.jobCode,
    tenant: data.loginInfo.tenant,
    landscape: data.loginInfo.landscape,
    tenantLandscape: getSHA3OfTenantLandscape(
      data.loginInfo.tenant,
      data.loginInfo.landscape
    ),
    title: data.jobTitle,
    description: data.jobDescription,
    jobTriggerFilePath: data.jobTriggerFilePath,
    hasOutputFile: boolean2_BooleanEnum(hasOutputFile(data.addedTasks)),
  };

  const createTaskInputs: CreateTaskInput[] = generateCreateTaskInputs(
    data.jobId,
    data.addedTasks
  );

  await createJob(createJobInput);

  for (let i = 0; i < createTaskInputs.length; i++) {
    await createTask(createTaskInputs[i]);
  }
};

export const callUpdateJob = async (
  data: {
    loginInfo: LoginInfo;
    jobId: string;
    jobTitle: string;
    jobCode: string | undefined;
    jobDescription: string;
    jobTriggerFilePath: string;
    addedTasks: TaskItemForDB[];
  },
  allTaskMetadatas: TaskMetadataItemForFront[]
) => {
  data.addedTasks = fixTaskMetadataVersion(allTaskMetadatas, data.addedTasks);

  const updateJobInput: UpdateJobInput = {
    id: data.jobId,
    jobCode: data.jobCode,
    title: data.jobTitle,
    description: data.jobDescription,
    jobTriggerFilePath: data.jobTriggerFilePath,
    hasOutputFile: boolean2_BooleanEnum(hasOutputFile(data.addedTasks)),
  };

  const createTaskInputs: CreateTaskInput[] = generateCreateTaskInputs(
    data.jobId,
    data.addedTasks
  );

  await updateJob(updateJobInput);

  let order = 0;
  let isFirstTaskDeletion = true;
  let deletionResult: DeleteTaskMutation | null = null;
  while (isFirstTaskDeletion || (deletionResult && deletionResult.deleteTask)) {
    isFirstTaskDeletion = false;
    const deleteTaskInput: DeleteTaskInput = {
      jobIdOrder: joinWithHash(data.jobId, order.toString()),
    };

    deletionResult = await deleteTask(deleteTaskInput);
    order++;
  }

  const createTaskPromises = createTaskInputs.map(
    async createTaskInput => await createTask(createTaskInput)
  );

  await Promise.all(createTaskPromises);
};

const generateCreateTaskInputs = (
  jobId: string,
  addedTasks: TaskItemForDB[]
) => {
  return addedTasks.map(task => {
    const createTaskInput: CreateTaskInput = {
      version: task.version,
      product: task.product,
      batch: task.batch,
      batchName: task.batchName,
      versionProductBatch: task.versionProductBatch,
      title: task.title,
      jobId: jobId,
      order: task.order,
      jobIdOrder: joinWithHash(jobId, task.order.toString()),
      args: R.map(
        R.pick(['order', 'option', 'value', 'replaceInFileName']),
        task.args
      ),
      includeInDownload: task.includeInDownload,
    };

    return createTaskInput;
  });
};

export const generateJobItem = (data: {
  loginInfo: LoginInfo;
  jobId: string;
  jobTitle: string;
  jobCode: string;
  jobDescription: string;
  jobTriggerFilePath: string;
  addedTasks: TaskItemForDB[];
}): JobItem => {
  const jobItem: JobItem = {
    id: data.jobId,
    jobCode: data.jobCode,
    tenant: data.loginInfo.tenant,
    landscape: data.loginInfo.landscape,
    tenantLandscape: getSHA3OfTenantLandscape(
      data.loginInfo.tenant,
      data.loginInfo.landscape
    ),
    title: data.jobTitle,
    description: data.jobDescription,
    jobTriggerFilePath: data.jobTriggerFilePath,
    tasks: {
      items: data.addedTasks as TaskItem[],
    },
    hasOutputFile: boolean2_BooleanEnum(hasOutputFile(data.addedTasks)),
  };

  return jobItem;
};

export const sortTasksByOrder = R.lift(
  R.over(R.lensPath(['tasks', 'items']), R.sortBy(R.prop('order')))
);

const removeSpacesAndChangeToLower = R.pipe(R.replace(/\s+/g, ''), R.toLower);

export const isJobTitleMatching = (searchWord: string) => {
  const formattedSearchWord = removeSpacesAndChangeToLower(searchWord);

  return R.pipe(
    R.path<any>(['title']),
    removeSpacesAndChangeToLower,
    R.includes(formattedSearchWord)
  );
};

const sortByTitle = R.sortBy(R.prop('title'));

const getFilteredJobs = (searchWord: string) =>
  R.filter(isJobTitleMatching(searchWord));

export const getSortedFilteredJobs = (searchWord: string, jobs: JobItem[]) =>
  sortByTitle(getFilteredJobs(searchWord)(jobs));

export const fixTaskMetadataVersion = (
  taskMetadatas: TaskMetadataItemForFront[],
  addedTasks: TaskItemForDB[]
) => {
  const addedTasksWithFixedVersion: TaskItemForDB[] = [];
  for (const addedTask of addedTasks) {
    const taskMetadataForTask = R.find(
      R.propEq('batch', addedTask.batch),
      taskMetadatas
    );

    const addedTaskWithFixedVersion = R.unless(
      R.always(R.isNil(taskMetadataForTask)),
      R.pipe(
        R.assoc('version', R.path(['version'])(taskMetadataForTask)),
        R.assoc(
          'versionProductBatch',
          R.path(['versionProductBatch'])(taskMetadataForTask)
        )
      )
    )(addedTask);

    addedTasksWithFixedVersion.push(addedTaskWithFixedVersion);
  }
  return addedTasksWithFixedVersion;
};
